/**
 * Парсер Excel/CSV для импорта приглашений (чистые функции, без React).
 * Используется UserInvitationsImport; unit-тесты — import-parse.test.ts.
 */

import type { InvitationChannel } from './types';
import type { InvitationItem } from './InvitationItemsBuilder';

// ============================================================
// Маппинг plan_type / «Тариф» из Excel → доступ (items)
// ============================================================
// Узбекский тенант (club): абонемент «Клуб» = SUBSCRIPTION_PLAN id=2.
//   Yillik (годовой)          → pricingTierId=9
//   Oilaviy yillik (семейный) → pricingTierId=19
//   Oylik / «разовый»         → pricingTierId=7
// Файлы вида muharram_uzbekistan_invites.xlsx пишут свободный текст
//   «Клуб / Yillik (tier id=9)» — парсер вытаскивает tier id=N.
// Legacy (другие тенанты): monthly/perfona → CHANNEL_PASS id=7.
// При смене ID на проде — править здесь.
export const UZ_CLUB_SUBSCRIPTION_PLAN_ID = 2;

export const PLAN_TYPE_TO_ITEM: Record<string, InvitationItem> = {
  yillik: {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 9,
  },
  oilaviy: {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 19,
  },
  'oilaviy yillik': {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 19,
  },
  // русские/узб. алиасы семейного тарифа (без tier id в ячейке)
  семейный: {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 19,
  },
  family: {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 19,
  },
  oylik: {
    targetType: 'SUBSCRIPTION_PLAN',
    targetId: UZ_CLUB_SUBSCRIPTION_PLAN_ID,
    pricingTierId: 7,
  },
  monthly: { targetType: 'CHANNEL_PASS', targetId: 7, pricingTierId: 15 },
  perfona: { targetType: 'CHANNEL_PASS', targetId: 7, pricingTierId: null },
};

/** pricingTierId семейных тарифов (isFamily=true). Oilaviy yillik = 19. */
export const FAMILY_PRICING_TIER_IDS = new Set<number>([
  PLAN_TYPE_TO_ITEM.oilaviy.pricingTierId as number,
]);

export function isFamilyPlanItems(items: InvitationItem[]): boolean {
  return items.some(
    (i) =>
      i.targetType === 'SUBSCRIPTION_PLAN' &&
      i.pricingTierId !== null &&
      FAMILY_PRICING_TIER_IDS.has(i.pricingTierId),
  );
}

/**
 * Нормализация фамилии для склейки пар: Soipova↔Soipov, Gulyamova↔Gulyamov.
 */
export function normalizeSurnameForPairing(lastName: string): string {
  let s = lastName.toLowerCase().normalize('NFC').trim();
  s = s.replace(/[''`ʼʻ]/g, '');
  if (s.endsWith('ovna') || s.endsWith('evna')) {
    return s.slice(0, -4);
  }
  if (s.endsWith('ova') || s.endsWith('eva') || s.endsWith('ina')) {
    return s.slice(0, -1);
  }
  return s;
}

/**
 * Семейный тариф (Oilaviy): в Excel люди идут ПАРАМИ подряд —
 * 1–2, 3–4, 5–6… (Soipova+Soipov, Sadullayev+Yunusova, …).
 * Первый = владелец подписки, второй = co-invite (не отдельный инвайт).
 *
 * - Явно заполненный Co-invite не трогаем (строка не участвует в склейке).
 * - Одинаковый контакт у пары (общий TG, Doniyor/Umida @DaniyarUmida) —
 *   оставляем одну строку владельца без co-invite.
 * - Нечётный «хвост» без пары — остаётся как одиночный инвайт.
 */
export function pairFamilyCoInvites(rows: ParsedImportRow[]): ParsedImportRow[] {
  if (rows.length < 2) {
    return rows;
  }

  // Индексы семейных строк без уже заданного co-invite — в порядке файла.
  const candidateIdx: number[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (isFamilyPlanItems(r.items) && !r.familyMemberContact.trim()) {
      candidateIdx.push(i);
    }
  }
  if (candidateIdx.length < 2) {
    return rows;
  }

  const skipAsMember = new Set<number>();
  const next = rows.map((r) => ({
    ...r,
    items: r.items.map((it) => ({ ...it })),
  }));

  for (let p = 0; p + 1 < candidateIdx.length; p += 2) {
    const ownerIdx = candidateIdx[p];
    const memberIdx = candidateIdx[p + 1];
    const ownerContact = next[ownerIdx].contact.trim();
    const memberContact = next[memberIdx].contact.trim();
    if (!memberContact) {
      continue;
    }

    const sameContact =
      ownerContact.toLowerCase().replace(/^@/, '') ===
      memberContact.toLowerCase().replace(/^@/, '');

    if (sameContact) {
      // Общий Telegram-аккаунт — один инвайт, без co-invite.
      skipAsMember.add(memberIdx);
      continue;
    }

    next[ownerIdx] = {
      ...next[ownerIdx],
      familyMemberContact: memberContact,
    };
    skipAsMember.add(memberIdx);
  }

  if (skipAsMember.size === 0) {
    return rows;
  }
  return next.filter((_, i) => !skipAsMember.has(i));
}

export interface ParsedImportRow {
  channel: InvitationChannel;
  contact: string;
  firstName: string;
  lastName: string;
  items: InvitationItem[];
  accessEndsAt: string;
  familyMemberContact: string;
}

export interface ParseExcelOptions {
  /** Имя листа / файла — фоллбэк тарифа, если в строках нет колонки «Тариф». */
  sheetName?: string;
}

/**
 * Резолв тарифа из ячейки Excel.
 * Приоритет:
 *  1) явный «tier id=N» / «pricingTierId=N» в свободном тексте;
 *  2) точный alias (yillik / oilaviy / oylik / monthly / perfona);
 *  3) substring-match по alias (длинные ключи раньше).
 */
export function resolvePlanItem(planRaw: string): InvitationItem | null {
  const raw = planRaw.trim();
  if (!raw) {return null;}
  const lower = raw.toLowerCase();

  const tierMatch =
    raw.match(/tier\s*id\s*=\s*(\d+)/i) ??
    raw.match(/pricing_?tier_?id\s*[=:]\s*(\d+)/i);
  if (tierMatch) {
    const pricingTierId = Number(tierMatch[1]);
    if (/channel[_\s-]?pass/i.test(raw)) {
      const cpMatch = raw.match(/channel[_\s-]?pass(?:\s*id)?\s*[=:]\s*(\d+)/i);
      return {
        targetType: 'CHANNEL_PASS',
        targetId: cpMatch ? Number(cpMatch[1]) : 7,
        pricingTierId,
      };
    }
    const planIdMatch = raw.match(
      /(?:plan|target|subscription(?:_?plan)?)\s*id\s*[=:]\s*(\d+)/i,
    );
    return {
      targetType: 'SUBSCRIPTION_PLAN',
      targetId: planIdMatch ? Number(planIdMatch[1]) : UZ_CLUB_SUBSCRIPTION_PLAN_ID,
      pricingTierId,
    };
  }

  const exact = PLAN_TYPE_TO_ITEM[lower];
  if (exact) {return { ...exact };}

  // Substring: длинные ключи первыми, чтобы «oilaviy yillik» / «семейный» победили «yillik».
  // Не матчим короткие ключи вроде «plan» — только алиасы длиной ≥4, кроме
  // «family» (6) / кириллицы «семейный».
  const aliases = Object.entries(PLAN_TYPE_TO_ITEM).sort(
    (a, b) => b[0].length - a[0].length,
  );
  for (const [key, item] of aliases) {
    if (key.length >= 4 && lower.includes(key)) {
      return { ...item };
    }
  }
  // «семья» / «family plan» без полного alias
  if (lower.includes('семь') || lower.includes('oilav')) {
    return { ...PLAN_TYPE_TO_ITEM.oilaviy };
  }
  return null;
}

/**
 * Фоллбэк тарифа по имени листа (для сплит-файлов 1_yillik / 2_oilaviy / 3_sardor).
 * Oilaviy проверяем раньше Yillik — иначе «Oilaviy yillik» сматчится как годовой.
 */
export function planFromSheetName(sheetName: string): InvitationItem | null {
  const lower = sheetName.toLowerCase();
  if (lower.includes('oilav') || lower.includes('семейн') || lower.includes('family')) {
    return { ...PLAN_TYPE_TO_ITEM.oilaviy };
  }
  if (lower.includes('sardor') || lower.includes('oylik') || lower.includes('месяц')) {
    return { ...PLAN_TYPE_TO_ITEM.oylik };
  }
  if (lower.includes('yillik') || lower.includes('yilliq') || lower.includes('годов')) {
    return { ...PLAN_TYPE_TO_ITEM.yillik };
  }
  return null;
}

/** Нормализация даты окончания доступа из Excel-ячейки. */
export function parseAccessEndsAt(raw: string): string | null {
  if (!raw) {return null;}
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }
  const dmy = raw.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (dmy) {
    const day = dmy[1].padStart(2, '0');
    const month = dmy[2].padStart(2, '0');
    return `${dmy[3]}-${month}-${day}`;
  }
  return null;
}

/** Маппинг Excel-строк в UI-формат, с авто-определением колонок. */
export function parseExcelRows(
  rows: Record<string, string>[],
  options: ParseExcelOptions = {},
): ParsedImportRow[] {
  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  const findCol = (...needles: string[]): string | null => {
    for (const h of headers) {
      const lower = h.toLowerCase();
      if (needles.some((n) => lower.includes(n))) {return h;}
    }
    return null;
  };

  const channelCol = findCol('канал', 'channel');
  const contactCol = findCol('контакт', 'contact', 'email', 'phone', 'username', 'телефон');
  // Отдельная колонка telegram user_id — fallback, если «Контакт» пуст.
  const userIdCol = findCol('user_id', 'userid', 'telegram_id', 'telegramid', 'tg_id', 'tgid');
  const firstCol = findCol('имя', 'first');
  const lastCol = findCol('фамил', 'last');
  // Тариф: «Тариф (проставить в UI)» / plan_type / plan.
  const planCol = findCol('plan_type', 'тариф', 'plan');
  const expiresCol = findCol('expires', 'expire', 'accessendsat', 'access_ends_at', 'срок');
  // Co-invite: второй член семьи для семейного тарифа.
  const coInviteCol = findCol(
    'co-invite',
    'coinvite',
    'co_invite',
    'family member',
    'family_member',
    'семь',
    '2-й член',
    '2й член',
  );

  const sheetDefaultPlan = options.sheetName
    ? planFromSheetName(options.sheetName)
    : null;

  const mapped = rows
    .map((row): ParsedImportRow => {
      const channelRaw = channelCol ? (row[channelCol] ?? '').toString().trim().toUpperCase() : '';
      const channel: InvitationChannel = channelRaw === 'EMAIL' ? 'EMAIL' : 'TELEGRAM';
      let contact = contactCol ? (row[contactCol] ?? '').toString().trim() : '';
      if (!contact && userIdCol) {
        contact = (row[userIdCol] ?? '').toString().trim();
      }
      const firstName = firstCol ? (row[firstCol] ?? '').toString().trim() : '';
      const lastName = lastCol ? (row[lastCol] ?? '').toString().trim() : '';

      const planRaw = planCol ? (row[planCol] ?? '').toString().trim() : '';
      const planItem = resolvePlanItem(planRaw) ?? sheetDefaultPlan;
      const items: InvitationItem[] = planItem ? [{ ...planItem }] : [];

      // Co-invite из колонки; иначе (для семейного) — pairFamilyCoInvites ниже.
      let familyMemberContact = coInviteCol
        ? (row[coInviteCol] ?? '').toString().trim()
        : '';

      // Иногда второй член пишут в «Комментарий»: co-invite: @user / +998…
      if (!familyMemberContact) {
        const commentCol = findCol('коммент', 'comment', 'note', 'примечан');
        if (commentCol) {
          const comment = (row[commentCol] ?? '').toString();
          const fromComment =
            comment.match(/co-?invite\s*[:=]?\s*(@[\w.]+|\+?\d{6,})/i) ??
            comment.match(/(?:второй|2-?й|жена|муж|супруг\w*)\s*[:=]?\s*(@[\w.]+|\+?\d{6,})/i);
          if (fromComment) {
            familyMemberContact = fromComment[1].trim();
          }
        }
      }

      const expiresRaw = expiresCol ? (row[expiresCol] ?? '').toString().trim() : '';
      const accessEndsAt = parseAccessEndsAt(expiresRaw) ?? '';

      return {
        channel,
        contact,
        firstName,
        lastName,
        items,
        accessEndsAt,
        familyMemberContact,
      };
    })
    .filter((r) => r.contact.length > 0);

  // Склеить семейные пары по фамилии (Soipova+Soipov → owner + co-invite).
  return pairFamilyCoInvites(mapped);
}
